import { isEqual } from 'lodash-es'
import localforage from '@ms/plugins/localforage.js'

const MAX_STORAGE_SIZE = 5 * 1024 * 1024 // 5MB

/**
 * Stores a value in either sessionStorage, localStorage, or localforage.
 *
 * @param {string} key - The key under which the value will be stored.
 * @param {*} value - The value to be stored. It will be stringified if stored in sessionStorage or localStorage.
 * @param {string} [type='db'] - The type of storage to use ('db', 'session', or 'local').
 * @param {number|null} [maxAge=null] - The maximum age (in milliseconds) for the cached item. If null, the item does not expire.
 * @returns {Promise<void>|void} - Returns a promise when storing in localforage, otherwise returns void.
 */
export const setStorage = async (key, value, type = 'db', maxAge = null) => {
  // 验证参数
  if (!key || typeof key !== 'string') {
    throw new Error('存储键必须是非空字符串')
  }
  if (type && !['db', 'session', 'local'].includes(type)) {
    throw new Error('无效的存储类型')
  }
  if (maxAge !== null && (!Number.isInteger(maxAge) || maxAge <= 0)) {
    throw new Error('maxAge必须是正整数')
  }

  try {
    const data = maxAge ? { value, timestamp: Date.now(), maxAge } : value

    // 检查数据大小
    const size = new Blob([JSON.stringify(data)]).size
    if (size > MAX_STORAGE_SIZE) {
      throw new Error('数据大小超过限制')
    }

    if (type === 'session') {
      return sessionStorage.setItem(key, JSON.stringify(data))
    } else if (type === 'local') {
      return localStorage.setItem(key, JSON.stringify(data))
    }
    const oldValue = await localforage.getItem(key)
    if (oldValue && isEqual(oldValue, data)) return
    await localforage.setItem(key, data)
  } catch (error) {
    console.error('setStorage failed:', error)
  }
}

/**
 * Retrieves a value from storage based on the provided key.
 *
 * @param {string} key - The key of the item to retrieve from storage.
 * @param {string} [type='db'] - The type of storage to use ('db', 'session', or 'local').
 * @returns {Promise<any|null>} - A promise that resolves to the parsed value from storage, or null if the item does not exist or an error occurs.
 */
export const getStorage = async (key, type = 'db') => {
  try {
    let data
    if (type === 'session') {
      data = sessionStorage.getItem(key)
    } else if (type === 'local') {
      data = localStorage.getItem(key)
    } else {
      data = await localforage.getItem(key)
    }
    if (!data) return null
    const parsedData = type === 'db' ? data : JSON.parse(data)
    if (parsedData.maxAge && Date.now() - parsedData.timestamp > parsedData.maxAge) {
      await removeStorage(key, type)
      return null
    }
    return parsedData.value ? parsedData.value : parsedData
  } catch (error) {
    console.error(error)
    return null
  }
}

/**
 * Removes a value from storage based on the provided key.
 *
 * @param {string} key - The key of the item to remove from storage.
 * @param {string} [type='db'] - The type of storage to use ('db', 'session', or 'local').
 * @returns {Promise<void>|void} - Returns a promise when removing from localforage, otherwise returns void.
 */
const removeStorage = async (key, type = 'db') => {
  if (type === 'session') {
    sessionStorage.removeItem(key)
  } else if (type === 'local') {
    localStorage.removeItem(key)
  } else {
    await localforage.removeItem(key)
  }
}

/**
 * Caches the response of a given promise using a specified cache key.
 *
 * @param {string} [cacheKey=''] - The key to use for caching the response.
 * @param {Promise} reqPromise - The promise whose response needs to be cached.
 * @param {Object} [options={}] - Additional options for caching.
 * @param {string} [options.type='db'] - The type of storage to use ('db', 'session', or 'local').
 * @param {number|null} [options.maxAge=null] - The maximum age (in milliseconds) for the cached item. If null, the item does not expire.
 * @returns {Promise} - A promise that resolves to the cached response or the original response.
 */
export default async function cacheResponse(cacheKey = '', reqPromise, { type = 'db', maxAge = null, force } = {}) {
  if (!cacheKey) return reqPromise
  const cachedData = await getStorage(cacheKey, type)

  if (!force && cachedData) {
    reqPromise.then((res) => {
      if (res) {
        if (!isEqual(cachedData, res)) {
          setStorage(cacheKey, res, type, maxAge)
        }
      }
      return res
    })
    return cachedData
  } else {
    const res = await reqPromise
    res && setStorage(cacheKey, res, type, maxAge)
    return res
  }
}
